import {Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {
    AnalyticsDefinition,
    CameraAnalyticsConfigResponse,
    CameraAnalyticsSubscriptionProvisioning,
    CameraCorrelationSettings,
    EffectiveCameraAnalytics,
} from '../../models/vte';
import {CameraFilterOption, OperationsFiltersService} from './operations-filters.service';
import {OperationsOrganizationService} from './operations-organization.service';
import {isVehicleDetectionAnalyticsKey, resolvePreferredVehicleDetectionKey,} from './vision-customer-labels';
import {CameraAnalyticsConfigPatchPayload, VteApiService} from './vte-api.service';

export type CameraAnalyticsRow = {
    configurationId: number | null;
    definition: AnalyticsDefinition;
    isEnabled: boolean;
    source: EffectiveCameraAnalytics['source'];
};

export type CameraAnalyticsConfigState = {
    cameraId: number;
    configurationSource: CameraAnalyticsConfigResponse['configuration_source'];
    rows: CameraAnalyticsRow[];
    correlationWindowSeconds: number;
    eenOauthConfigured: boolean;
    subscriptionProvisioning: CameraAnalyticsSubscriptionProvisioning;
};

@Injectable({
    providedIn: 'root',
})
export class CameraAnalyticsConfigViewService {
    constructor(
        private vteApi: VteApiService,
        private filtersService: OperationsFiltersService,
        private organizationService: OperationsOrganizationService,
    ) {
    }

    loadCameraOptions(orgId?: number | string): Observable<CameraFilterOption[]> {
        return this.filtersService.loadCameraOptions(orgId);
    }

    resolveOrganizationId(selectedOrgId?: string | null): number | null {
        if (this.organizationService.canFilterByOrganization() && selectedOrgId?.trim()) {
            const parsed = Number(selectedOrgId);
            return Number.isNaN(parsed) ? null : parsed;
        }
        return this.organizationService.getCurrentOrganizationId();
    }

    canFilterByOrganization(): boolean {
        return this.organizationService.canFilterByOrganization();
    }

    loadOrganizationOptions() {
        return this.organizationService.loadOrganizationOptions();
    }

    loadConfiguration(cameraId: number): Observable<CameraAnalyticsConfigState> {
        return forkJoin({
            analytics: this.vteApi.getCameraAnalyticsConfig(cameraId).pipe(
                catchError(() => of({
                    configuration_source: 'none',
                    analytics_configurations: [],
                    effective_analytics: [],
                    een_oauth_configured: false,
                    subscription_provisioning: {
                        status: 'not_required',
                        detail: 'Unable to load subscription provisioning status.',
                    },
                } as CameraAnalyticsConfigResponse)),
            ),
            correlation: this.vteApi.getCameraCorrelationSettings(cameraId).pipe(
                catchError(() => of({correlation_window_seconds: 30})),
            ),
        }).pipe(
            map(({
                     analytics,
                     correlation
                 }) => this.buildState(cameraId, analytics, correlation)),
        );
    }

    buildAnalyticsPatch(rows: CameraAnalyticsRow[]): CameraAnalyticsConfigPatchPayload {
        return {
            analytics_configurations: rows.map((row) => ({
                analytics_definition: row.definition.id,
                is_enabled: row.isEnabled,
            })),
        };
    }

    buildCorrelationPatch(correlationWindowSeconds: number): Partial<CameraCorrelationSettings> {
        return {
            correlation_window_seconds: correlationWindowSeconds,
        };
    }

    /**
     * Persist camera analytics. When the camera has no CAC yet (source=none),
     * create explicit configuration first (existing POST), then PATCH selections.
     */
    saveConfiguration(
        cameraId: number,
        rows: CameraAnalyticsRow[],
        correlationWindowSeconds: number,
        configurationSource: CameraAnalyticsConfigResponse['configuration_source'] = 'none',
    ): Observable<CameraAnalyticsConfigState> {
        const persist$ = () => forkJoin({
            analytics: this.vteApi.patchCameraAnalyticsConfig(
                cameraId,
                this.buildAnalyticsPatch(rows),
            ),
            correlation: this.vteApi.patchCameraCorrelationSettings(
                cameraId,
                this.buildCorrelationPatch(correlationWindowSeconds),
            ),
        }).pipe(
            map(({analytics, correlation}) =>
                this.buildState(cameraId, analytics, correlation),
            ),
        );

        if (configurationSource === 'override') {
            return persist$();
        }

        return this.vteApi.createCameraAnalyticsOverride(cameraId).pipe(
            mergeMap(() => persist$()),
        );
    }

    /** Clear all CameraAnalyticsConfiguration rows → effective analytics NONE. */
    clearConfiguration(
        cameraId: number,
        correlationWindowSeconds: number,
    ): Observable<CameraAnalyticsConfigState> {
        return forkJoin({
            analytics: this.vteApi.resetCameraAnalyticsOverride(cameraId),
            correlation: this.vteApi.getCameraCorrelationSettings(cameraId).pipe(
                catchError(() => of({correlation_window_seconds: correlationWindowSeconds})),
            ),
        }).pipe(
            map(({analytics, correlation}) => this.buildState(cameraId, analytics, correlation)),
        );
    }

    hasExplicitConfiguration(
        source: CameraAnalyticsConfigResponse['configuration_source'] | null | undefined,
    ): boolean {
        return source === 'override';
    }

    toggleRow(rows: CameraAnalyticsRow[], definitionId: number, enabled: boolean): CameraAnalyticsRow[] {
        const target = rows.find((row) => row.definition.id === definitionId);
        if (!target) {
            return rows;
        }

        if (!isVehicleDetectionAnalyticsKey(target.definition.analytics_key)) {
            return rows.map((row) =>
                row.definition.id === definitionId ? {...row, isEnabled: enabled} : row,
            );
        }

        // Mutual exclusivity: enabling one vehicle-detection provider clears the other.
        // Disabling is allowed — zero vehicle providers is valid.
        if (!enabled) {
            return rows.map((row) =>
                row.definition.id === definitionId ? {...row, isEnabled: false} : row,
            );
        }

        return rows.map((row) => {
            if (!isVehicleDetectionAnalyticsKey(row.definition.analytics_key)) {
                return row;
            }
            return {
                ...row,
                isEnabled: row.definition.analytics_key === target.definition.analytics_key,
            };
        });
    }

    /**
     * Vehicle Detection providers remain distinct in the UI (mutually exclusive checkboxes).
     * When both are somehow enabled, keep only the preferred provider visible as enabled.
     * Decision A: when neither is enabled, leave both disabled — do not invent V3 for display.
     */
    groupRowsForCustomer(rows: CameraAnalyticsRow[]): CameraAnalyticsRow[] {
        const enabledVehicleKeys = rows
            .filter((row) => row.isEnabled && isVehicleDetectionAnalyticsKey(row.definition.analytics_key))
            .map((row) => row.definition.analytics_key);

        if (enabledVehicleKeys.length <= 1) {
            return rows;
        }

        const preferredKey = resolvePreferredVehicleDetectionKey(enabledVehicleKeys);
        if (!preferredKey) {
            return rows;
        }

        return rows.map((row) => {
            if (!isVehicleDetectionAnalyticsKey(row.definition.analytics_key)) {
                return row;
            }
            return {
                ...row,
                isEnabled: row.definition.analytics_key === preferredKey,
            };
        });
    }

    private buildState(
        cameraId: number,
        response: CameraAnalyticsConfigResponse,
        correlation: CameraCorrelationSettings,
    ): CameraAnalyticsConfigState {
        const explicitByDefinitionId = new Map(
            (response.analytics_configurations || []).map((configuration) => [
                configuration.analytics_definition,
                configuration,
            ]),
        );
        const effectiveRows = (response.effective_analytics || []).map((entry) => ({
            configurationId: explicitByDefinitionId.get(entry.analytics_definition.id)?.id ?? null,
            definition: entry.analytics_definition,
            isEnabled: entry.is_enabled,
            source: entry.source,
        }));

        return {
            cameraId,
            configurationSource: response.configuration_source || 'none',
            rows: effectiveRows,
            correlationWindowSeconds: correlation.correlation_window_seconds ?? 30,
            eenOauthConfigured: !!response.een_oauth_configured,
            subscriptionProvisioning: response.subscription_provisioning || {
                status: 'not_required',
            },
        };
    }
}
